// src/components/AgentActionsPanel.js
// Painel de ações AI-native para uma empresa. Mostra 3 botões (um por agente
// do Sprint 1) + resultado inline do último invoke. Pensado para ser embutido
// no HistoryModal na seção da empresa.

import React, { useState, useEffect } from 'react';
import { Sparkles, Users, TrendingUp, ChevronDown, ChevronRight, Loader2, UserCheck, ExternalLink, FileSearch } from 'lucide-react';
import { useAgent } from '../hooks/useAgent';
import { supabase } from '../supabaseClient';

const CURRENT_USER_EMAIL = 'eduardo@astoncapital.com.br'; // TODO: trocar por auth real quando disponível

export default function AgentActionsPanel({ empresa }) {
  if (!empresa?.id) return null;

  return (
    <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-violet-600" />
        <h3 className="font-semibold text-sm text-violet-900">Agentes IA — Sprint 1</h3>
      </div>

      <BrieferCard empresa={empresa} />
      <DecisoresCard empresa={empresa} />
      <PendingCandidatesCard empresaId={empresa.id} />

      <RecentAgentEvidences empresaId={empresa.id} />
    </div>
  );
}

// ---------------- Briefer ----------------
function BrieferCard({ empresa }) {
  const { invoke, loading, lastResult, error } = useAgent('briefer');
  const [expanded, setExpanded] = useState(false);

  const run = async () => {
    await invoke({ empresa_id: empresa.id, decided_by: CURRENT_USER_EMAIL });
    setExpanded(true);
  };

  const briefing = lastResult?.briefing;

  return (
    <div className="bg-white border border-violet-200 rounded-md">
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <div className="text-sm font-medium flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-violet-600" />
              Briefing executivo
            </div>
            <p className="text-[11px] text-slate-500">
              Consolida Apollo + decisores + ofertas + notícias web. Grava em deal_context se houver ofertas ativas.
            </p>
          </div>
          <button
            onClick={run}
            disabled={loading}
            className="shrink-0 px-3 py-1 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1"
          >
            {loading ? <><Loader2 className="w-3 h-3 animate-spin" /> Analisando...</> : 'Gerar'}
          </button>
        </div>

        {error && <div className="text-[11px] text-red-700 bg-red-50 rounded px-2 py-1">⚠️ {error}</div>}

        {briefing && (
          <div className="mt-2 text-xs">
            <button onClick={() => setExpanded((v) => !v)} className="flex items-center gap-1 text-violet-700 hover:text-violet-900">
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {expanded ? 'ocultar' : 'ver briefing'}
            </button>
            {expanded && (
              <div className="mt-2 space-y-2 text-slate-700 leading-relaxed max-h-[60vh] overflow-y-auto pr-2">
                <div className="bg-violet-50 border border-violet-100 rounded p-2">
                  <strong className="text-[11px] text-violet-900 uppercase tracking-wide">Resumo executivo</strong>
                  <p className="mt-1">{briefing.resumo_executivo || '—'}</p>
                </div>
                {briefing.perfil_compra && (
                  <Section title="Perfil de compra" content={briefing.perfil_compra} />
                )}
                {briefing.sinais_intent && (
                  <Section title="Sinais de intent" content={briefing.sinais_intent} />
                )}
                {briefing.proximos_passos && (
                  <Section title="Próximos passos" content={briefing.proximos_passos} />
                )}
                {briefing.metadata_briefing?.lacunas_de_info?.length > 0 && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 rounded px-2 py-1">
                    ⚠️ Lacunas: {briefing.metadata_briefing.lacunas_de_info.join(', ')}
                  </div>
                )}
                <div className="text-[10px] text-slate-400">
                  {lastResult.web_searches_used > 0 && `🔍 ${lastResult.web_searches_used} web search(es) · `}
                  {lastResult.tokens_in + lastResult.tokens_out} tokens · {Math.round(lastResult.latency_ms / 100) / 10}s
                  {lastResult.deal_contexts_created > 0 && ` · 📝 gravou em ${lastResult.deal_contexts_created} oferta(s)`}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, content }) {
  const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
  return (
    <div>
      <strong className="text-[11px] text-slate-600 uppercase tracking-wide">{title}</strong>
      <p className="mt-0.5 whitespace-pre-wrap">{text}</p>
    </div>
  );
}

// ---------------- Decisores ----------------
function DecisoresCard({ empresa }) {
  const { invoke, loading, lastResult, error } = useAgent('decisores');
  const [mode, setMode] = useState('classify_existing');
  const [autoApply, setAutoApply] = useState(true);

  const canDiscover = !!empresa.apollo_id;
  const effectiveMode = (mode === 'discover_new' || mode === 'both') && !canDiscover ? 'classify_existing' : mode;

  const run = async () => {
    await invoke({
      empresa_id: empresa.id,
      mode: effectiveMode,
      decided_by: CURRENT_USER_EMAIL,
      auto_apply: autoApply,
    });
  };

  return (
    <div className="bg-white border border-violet-200 rounded-md p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-sm font-medium flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-indigo-600" />
            Mapear decisores
          </div>
          <p className="text-[11px] text-slate-500">
            Classifica senioridade dos contatos existentes. "discover_new" busca C-suite/VP/Director novos no Apollo (requer apollo_id).
          </p>
        </div>
        <button
          onClick={run}
          disabled={loading}
          className="shrink-0 px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
        >
          {loading ? <><Loader2 className="w-3 h-3 animate-spin" /> Analisando...</> : 'Executar'}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 text-[11px] mb-1">
        <label className="flex items-center gap-1">
          <input type="radio" name={`dm-mode-${empresa.id}`} checked={mode === 'classify_existing'} onChange={() => setMode('classify_existing')} />
          classificar existentes
        </label>
        <label className={`flex items-center gap-1 ${!canDiscover ? 'text-slate-400' : ''}`}>
          <input type="radio" name={`dm-mode-${empresa.id}`} checked={mode === 'discover_new'} onChange={() => setMode('discover_new')} disabled={!canDiscover} />
          descobrir novos
        </label>
        <label className={`flex items-center gap-1 ${!canDiscover ? 'text-slate-400' : ''}`}>
          <input type="radio" name={`dm-mode-${empresa.id}`} checked={mode === 'both'} onChange={() => setMode('both')} disabled={!canDiscover} />
          ambos
        </label>
        <label className="flex items-center gap-1 ml-auto">
          <input type="checkbox" checked={autoApply} onChange={(e) => setAutoApply(e.target.checked)} />
          aplicar ≥0.85 conf.
        </label>
      </div>

      {!canDiscover && (mode === 'discover_new' || mode === 'both') && (
        <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1 mb-1">
          Empresa sem apollo_id — só classificação de existentes estará ativa.
        </div>
      )}

      {error && <div className="text-[11px] text-red-700 bg-red-50 rounded px-2 py-1">⚠️ {error}</div>}

      {lastResult?.ok && (
        <div className="mt-2 text-[11px] space-y-1">
          {lastResult.classify_result && (
            <div className="text-slate-700">
              <strong>Classificação:</strong> {lastResult.classify_result.total_to_classify} contatos →{' '}
              <span className="text-emerald-700">{lastResult.classify_result.applied} aplicados</span>
              {lastResult.classify_result.pending_evidences > 0 && (
                <>, <span className="text-amber-700">{lastResult.classify_result.pending_evidences} pendentes (F8)</span></>
              )}
            </div>
          )}
          {lastResult.discover_result && !lastResult.discover_result.skipped && (
            <div className="text-slate-700">
              <strong>Discover:</strong> {lastResult.discover_result.apollo_candidates} candidatos Apollo →{' '}
              <span className="text-emerald-700">{lastResult.discover_result.added} adicionados</span>
              {lastResult.discover_result.duplicates_detected > 0 && (
                <>, {lastResult.discover_result.duplicates_detected} duplicatas detectadas</>
              )}
            </div>
          )}
          {lastResult.discover_result?.skipped && (
            <div className="text-[11px] text-slate-500">Discover pulado: {lastResult.discover_result.reason}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------- Intent ----------------
// DESCONECTADO 22/abr/2026: agent_apollo_intent_monitor com ai_config.ativo=false
// desde 21/abr. Componente preservado para possível reativação futura.
// eslint-disable-next-line no-unused-vars
function IntentCard({ empresa }) {
  const { invoke, loading, lastResult, error } = useAgent('intent');
  const disabled = !empresa.apollo_id;

  const run = async () => {
    await invoke({
      empresa_id: empresa.id,
      decided_by: CURRENT_USER_EMAIL,
      auto_apply: true,
    });
  };

  return (
    <div className="bg-white border border-violet-200 rounded-md p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-sm font-medium flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
            Monitor de intent (job postings)
          </div>
          <p className="text-[11px] text-slate-500">
            Busca vagas ativas no Apollo e classifica como sinais de M&A/expansão/real estate deals. 1 crédito Apollo.
          </p>
        </div>
        <button
          onClick={run}
          disabled={loading || disabled}
          title={disabled ? 'Requer apollo_id — enriqueça a empresa primeiro' : ''}
          className="shrink-0 px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          {loading ? <><Loader2 className="w-3 h-3 animate-spin" /> Analisando...</> : 'Analisar'}
        </button>
      </div>

      {disabled && (
        <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1 mb-1">
          Esta empresa não tem apollo_id. Rode o enriquecimento antes.
        </div>
      )}

      {error && <div className="text-[11px] text-red-700 bg-red-50 rounded px-2 py-1">⚠️ {error}</div>}

      {lastResult?.ok && (
        <div className="mt-2 text-[11px]">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-lg font-bold text-emerald-700">
              {Math.round((lastResult.overall_assessment?.overall_intent_score || 0) * 100)}
            </span>
            <span className="text-slate-500">intent score</span>
            <span className="ml-auto text-slate-400">{lastResult.jobs_analyzed}/{lastResult.total_jobs} jobs</span>
          </div>
          <p className="text-slate-700 mb-1">{lastResult.overall_assessment?.summary || '—'}</p>
          <div className="text-slate-500">
            Ação recomendada: <strong className="text-slate-700">{lastResult.overall_assessment?.recommended_action || '—'}</strong>
            {lastResult.flagged_for_f8 && (
              <span className="ml-2 text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">🚩 revisão F8</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Pending candidates (discover_new) ----------------
// Lê evidences com field_name='pending_discover_candidates' do
// agent decision_maker_mapper_v1 e mostra os candidatos aguardando
// decisão humana. Shape real do evidence.suggested_value:
//   { empresa_nome, pending_count, candidates: [{ apollo_person_id,
//     nome, title, has_email, priority, reasoning }] }
// Fallback p/ pending_candidates_sample caso agente venha a mudar o key.
//
// TODO agent v1.0.2: gerar 1 evidence por candidato (com entity_type=
// 'contact' + entity_id=pending-uuid) pra permitir aprovar/rejeitar
// granularmente via apply-evidence. Hoje o evidence é consolidado e
// field_name='pending_discover_candidates' não é coluna de empresas,
// então apply-evidence falharia no UPDATE — por isso as ações por
// candidato aqui são apenas "Ver no Apollo" (read-only). Decisão
// global (aprovar todos / rejeitar todos) deve ir pro Painel F8.
function PendingCandidatesCard({ empresaId }) {
  const [evidences, setEvidences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('ai_evidence')
        .select('id, suggested_value, reasoning, created_at, confidence')
        .eq('entity_type', 'company')
        .eq('entity_id', empresaId)
        .eq('agent', 'decision_maker_mapper_v1')
        .eq('field_name', 'pending_discover_candidates')
        .eq('human_decision', 'pending')
        .order('created_at', { ascending: false })
        .limit(1);
      if (!cancelled) {
        setEvidences(data || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [empresaId]);

  if (loading) return null;
  if (evidences.length === 0) return null;

  const ev = evidences[0];
  const sv = ev.suggested_value || {};
  const candidates = Array.isArray(sv.candidates)
    ? sv.candidates
    : Array.isArray(sv.pending_candidates_sample)
      ? sv.pending_candidates_sample
      : [];
  if (candidates.length === 0) return null;

  const total = typeof sv.pending_count === 'number' ? sv.pending_count : candidates.length;
  const shown = candidates.slice(0, 5);

  const priorityBadge = (p) => {
    if (p === 'high')   return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (p === 'medium') return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };
  const priorityLabel = (p) => (p === 'high' ? 'alta' : p === 'medium' ? 'média' : 'baixa');

  return (
    <div className="bg-white border border-violet-200 rounded-md p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <UserCheck className="w-3.5 h-3.5 text-amber-600" />
        <div className="text-sm font-medium">
          Candidatos pendentes de decisão ({total})
        </div>
        <span className="ml-auto text-[10px] text-slate-400">
          gerado {new Date(ev.created_at).toLocaleDateString('pt-BR')}
        </span>
      </div>

      {ev.reasoning && (
        <p className="text-[11px] text-slate-500 mb-2">{ev.reasoning}</p>
      )}

      <ul className="space-y-2">
        {shown.map((c, i) => (
          <li
            key={c.apollo_person_id || i}
            className="border border-slate-200 rounded p-2 text-xs"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800 truncate">
                  {c.nome || '—'}
                  {c.title && <span className="text-slate-500 font-normal"> · {c.title}</span>}
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${priorityBadge(c.priority)}`}>
                    prioridade {priorityLabel(c.priority)}
                  </span>
                  {c.has_email && (
                    <span className="text-[10px] text-slate-500">📧 tem email</span>
                  )}
                </div>
                {c.reasoning && (
                  <p className="mt-1 text-[11px] text-slate-600 leading-snug">{c.reasoning}</p>
                )}
              </div>
              <div className="shrink-0 flex flex-col gap-1 items-end">
                <button
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('open-f8-modal', {
                      detail: { empresaId, focusEvidenceId: ev.id }
                    }));
                  }}
                  className="text-[11px] text-white bg-indigo-600 hover:bg-indigo-700 px-2 py-0.5 rounded flex items-center gap-0.5"
                  title="Abrir Painel F8 com contexto desta empresa"
                >
                  <FileSearch size={10} /> F8
                </button>
                {c.apollo_person_id && (
                  <a
                    href={`https://app.apollo.io/#/people/${c.apollo_person_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-slate-500 hover:text-slate-700 flex items-center gap-0.5"
                    title="Ver perfil no Apollo (cross-reference)"
                  >
                    Apollo <ExternalLink size={9} />
                  </a>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {candidates.length > 5 && (
        <div className="text-[11px] text-slate-500 mt-2">
          + {candidates.length - 5} candidato(s) — revise no Painel F8
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500">
        Aprovar/rejeitar individualmente requer <code className="font-mono">agent-decision-maker-mapper v1.0.2</code>.
        Decisão global disponível no Painel F8 (botão no Dashboard).
      </div>
    </div>
  );
}

// ---------------- Últimas evidences ----------------
function RecentAgentEvidences({ empresaId }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('ai_evidence')
        .select('id, agent, field_name, confidence, reasoning, created_at, human_decision')
        .eq('entity_type', 'company')
        .eq('entity_id', empresaId)
        .in('agent', [
          'company_research_briefer_v1',
          'decision_maker_mapper_v1',
        ])
        .order('created_at', { ascending: false })
        .limit(5);
      if (!cancelled) setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [empresaId]);

  if (rows.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-violet-200">
      <div className="text-[10px] uppercase tracking-wide text-violet-700 mb-1">Últimas execuções</div>
      <ul className="text-[11px] space-y-0.5">
        {rows.map((r) => (
          <li key={r.id} className="flex items-start gap-2">
            <span className="font-mono text-slate-500 shrink-0 w-[60px]">
              {new Date(r.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="text-slate-400 shrink-0">{r.agent.replace(/_v\d+$/, '')}</span>
            <span className="text-slate-700 truncate flex-1">{r.reasoning || '—'}</span>
            {r.human_decision === 'pending' && (
              <span className="text-amber-700 shrink-0">F8</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
